/**
 * useUserPreferences — hydrate + sync user preferences từ Supabase profiles.
 *
 * Strategy hybrid: localStorage cache cho instant read, Supabase source of truth
 * cho cross-device persistence.
 *
 * Columns synced (migration: scripts/user-preferences-onboarding.sql):
 *   profiles.preferred_industry      → localStorage.tour_industry
 *   profiles.preferred_source_lang   → localStorage.tour_source_lang
 *   profiles.preferred_target_lang   → localStorage.tour_target_lang
 *   profiles.onboarding_completed_at → localStorage.tour_completed
 *   profiles.onboarding_last_step    → localStorage.tour_last_step
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabase/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export interface UserPreferences {
  preferred_industry: string | null;
  preferred_source_lang: string;
  preferred_target_lang: string;
  onboarding_completed_at: string | null;
  onboarding_last_step: number;
}

const LS_KEYS = {
  industry: 'tour_industry',
  sourceLang: 'tour_source_lang',
  targetLang: 'tour_target_lang',
  completed: 'tour_completed',
  lastStep: 'tour_last_step',
} as const;

const DEFAULTS: UserPreferences = {
  preferred_industry: null,
  preferred_source_lang: 'vi',
  preferred_target_lang: 'en',
  onboarding_completed_at: null,
  onboarding_last_step: 0,
};

/** Read localStorage state synchronously (for instant UI) */
function readLocal(): UserPreferences {
  if (typeof window === 'undefined') return DEFAULTS;
  return {
    preferred_industry: localStorage.getItem(LS_KEYS.industry),
    preferred_source_lang: localStorage.getItem(LS_KEYS.sourceLang) || DEFAULTS.preferred_source_lang,
    preferred_target_lang: localStorage.getItem(LS_KEYS.targetLang) || DEFAULTS.preferred_target_lang,
    onboarding_completed_at: localStorage.getItem(LS_KEYS.completed) === '1' ? 'local' : null,
    onboarding_last_step: Number(localStorage.getItem(LS_KEYS.lastStep)) || 0,
  };
}

/** Write localStorage cache */
function writeLocal(prefs: Partial<UserPreferences>) {
  if (typeof window === 'undefined') return;
  if (prefs.preferred_industry !== undefined) {
    if (prefs.preferred_industry) localStorage.setItem(LS_KEYS.industry, prefs.preferred_industry);
    else localStorage.removeItem(LS_KEYS.industry);
  }
  if (prefs.preferred_source_lang) localStorage.setItem(LS_KEYS.sourceLang, prefs.preferred_source_lang);
  if (prefs.preferred_target_lang) localStorage.setItem(LS_KEYS.targetLang, prefs.preferred_target_lang);
  if (prefs.onboarding_completed_at !== undefined) {
    if (prefs.onboarding_completed_at) localStorage.setItem(LS_KEYS.completed, '1');
    else localStorage.removeItem(LS_KEYS.completed);
  }
  if (prefs.onboarding_last_step !== undefined) {
    localStorage.setItem(LS_KEYS.lastStep, String(prefs.onboarding_last_step));
  }
}

export function useUserPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UserPreferences>(readLocal);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // On mount + user change: hydrate from Supabase, MIRROR to localStorage (Supabase is source of truth)
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('preferred_industry, preferred_source_lang, preferred_target_lang, onboarding_completed_at, onboarding_last_step')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.warn('[useUserPreferences] Supabase read failed (migration pending?):', error.message);
          setIsLoading(false);
          return;
        }

        // ALWAYS overwrite local state from Supabase (handle case: new user with empty profile)
        // Avoid stale localStorage from previous user trên cùng browser
        const merged: UserPreferences = {
          preferred_industry: data?.preferred_industry ?? null,
          preferred_source_lang: data?.preferred_source_lang || DEFAULTS.preferred_source_lang,
          preferred_target_lang: data?.preferred_target_lang || DEFAULTS.preferred_target_lang,
          onboarding_completed_at: data?.onboarding_completed_at ?? null,
          onboarding_last_step: data?.onboarding_last_step ?? 0,
        };
        setPrefs(merged);

        // Force write — including null/empty to clear stale data từ user trước
        if (typeof window !== 'undefined') {
          if (merged.preferred_industry) localStorage.setItem(LS_KEYS.industry, merged.preferred_industry);
          else localStorage.removeItem(LS_KEYS.industry);

          localStorage.setItem(LS_KEYS.sourceLang, merged.preferred_source_lang);
          localStorage.setItem(LS_KEYS.targetLang, merged.preferred_target_lang);

          if (merged.onboarding_completed_at) localStorage.setItem(LS_KEYS.completed, '1');
          else localStorage.removeItem(LS_KEYS.completed);

          localStorage.setItem(LS_KEYS.lastStep, String(merged.onboarding_last_step));
        }
      } catch (e) {
        console.warn('[useUserPreferences] hydrate failed:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  /** Update preferences — write-through both Supabase + localStorage */
  const updatePreferences = useCallback(async (partial: Partial<UserPreferences>) => {
    // Optimistic local write first
    writeLocal(partial);
    setPrefs(prev => ({ ...prev, ...partial }));

    if (!user?.id) return; // Not logged in — localStorage only

    setIsSyncing(true);
    try {
      const updatePayload: Record<string, any> = {};
      if (partial.preferred_industry !== undefined) updatePayload.preferred_industry = partial.preferred_industry;
      if (partial.preferred_source_lang) updatePayload.preferred_source_lang = partial.preferred_source_lang;
      if (partial.preferred_target_lang) updatePayload.preferred_target_lang = partial.preferred_target_lang;
      if (partial.onboarding_completed_at !== undefined) updatePayload.onboarding_completed_at = partial.onboarding_completed_at;
      if (partial.onboarding_last_step !== undefined) updatePayload.onboarding_last_step = partial.onboarding_last_step;

      if (Object.keys(updatePayload).length === 0) return;

      const { error } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', user.id);

      if (error) {
        console.warn('[useUserPreferences] Supabase update failed:', error.message);
      }
    } catch (e) {
      console.warn('[useUserPreferences] update failed:', e);
    } finally {
      setIsSyncing(false);
    }
  }, [user?.id]);

  /** Shortcut — mark tour complete với industry + langs */
  const markTourComplete = useCallback(async (choices: {
    industry: string;
    sourceLang: string;
    targetLang: string;
  }) => {
    await updatePreferences({
      preferred_industry: choices.industry,
      preferred_source_lang: choices.sourceLang,
      preferred_target_lang: choices.targetLang,
      onboarding_completed_at: new Date().toISOString(),
      onboarding_last_step: 7, // 7 = last step sidebar tour
    });
  }, [updatePreferences]);

  /** Shortcut — track current step reached (for funnel analytics) */
  const updateLastStep = useCallback((step: number) => {
    // Only update forward progress, don't regress
    if (step > prefs.onboarding_last_step) {
      updatePreferences({ onboarding_last_step: step });
    }
  }, [prefs.onboarding_last_step, updatePreferences]);

  return {
    prefs,
    isLoading,
    isSyncing,
    updatePreferences,
    markTourComplete,
    updateLastStep,
  };
}
